import { useCallback, useMemo, useState, type FormEvent } from 'react';
import { PASSWORD_MIN_LENGTH } from '@repo/auth-contracts';
import { Alert } from '@/components/alert';
import { AuthCard } from '@/components/auth-card';
import { Button } from '@/components/button';
import { PageFooter } from '@/components/page-footer';
import { PasswordField } from '@/components/password-field';
import { env } from '@/lib/env';
import { InteractionApiError } from '@/lib/interaction-api';
import { useLocale } from '@/lib/locale-context';
import { copyFor, messageFor } from '@/lib/messages';
import { createRecoveryApi } from '@/lib/recovery-api';
import { useRecoveryCsrf } from './use-recovery-csrf';
import { BackToSignIn } from './back-to-sign-in';

/**
 * Sets the new password from the emailed link. The server answers `ok` for a
 * stale, spent, or invented token exactly as it does for a good one — so this
 * screen's success state means "we are done here", not "the token was real"
 * (docs/auth/40-features/password-reset.md AC5/AC6). The copy says so.
 */
export function ResetPassword({ token }: { token: string }) {
  const { locale } = useLocale();
  const copy = useMemo(() => copyFor(locale), [locale]);
  const api = useMemo(() => createRecoveryApi(env.authApiUrl), []);
  const { csrfToken, bootstrapError, retryBootstrap } = useRecoveryCsrf(api);
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const usable = password.length >= PASSWORD_MIN_LENGTH;

  const submit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      if (!usable || !csrfToken) return;
      setPending(true);
      setErrorCode(null);
      try {
        await api.complete(token, password, csrfToken);
        setDone(true);
      } catch (error) {
        setErrorCode(
          error instanceof InteractionApiError ? error.code : 'unknown',
        );
      } finally {
        setPending(false);
      }
    },
    [api, csrfToken, password, token, usable],
  );

  const footer = <PageFooter />;

  if (done) {
    return (
      <AuthCard title={copy.resetDoneTitle} footer={footer}>
        <p role="status" className="text-sm leading-relaxed text-buds-muted">
          {copy.resetDoneBody}
        </p>
        <BackToSignIn label={copy.backToSignIn} />
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title={copy.resetTitle}
      subtitle={copy.resetSubtitle}
      footer={footer}
    >
      <form onSubmit={(e) => void submit(e)} noValidate className="space-y-5">
        {bootstrapError ? (
          <div className="space-y-3">
            <Alert tone="error">{messageFor(bootstrapError, locale)}</Alert>
            <Button type="button" variant="outline" onClick={retryBootstrap}>
              {copy.errorRetry}
            </Button>
          </div>
        ) : null}
        {errorCode ? (
          <Alert tone="error">{messageFor(errorCode, locale)}</Alert>
        ) : null}
        <PasswordField
          id="new-password"
          label={copy.passwordNewLabel}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          showLabel={copy.passwordShow}
          hideLabel={copy.passwordHide}
          hint={copy.passwordHint}
          autoFocus
        />
        {/* Disabled until the CSRF token is in hand — see forgot-password. */}
        <Button
          type="submit"
          loading={pending}
          disabled={!usable || !csrfToken}
        >
          {copy.resetSubmit}
        </Button>
      </form>
      <BackToSignIn label={copy.backToSignIn} />
    </AuthCard>
  );
}
